import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

export const MCPOAuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('Processing OAuth callback...');

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setStatus('error');
      setMessage(`OAuth Error: ${errorParam}`);
      return;
    }

    if (!code) {
      setStatus('error');
      setMessage('Missing authorization code in callback URL.');
      return;
    }

    // Success case
    setMessage(`Authorization successful! Code: ${code}, State: ${state || 'N/A'}`);
    setStatus('success');

    // Auto-close window after 3 seconds
    setTimeout(() => {
      window.close();
    }, 3000);

  }, [searchParams]);

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)',
      padding: '1rem'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '28rem',
        backgroundColor: 'var(--color-surface)',
        borderRadius: '0.75rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        padding: '1.5rem',
        textAlign: 'center'
      }}>
        <div style={{ paddingBottom: '1.5rem' }}>
          {status === 'loading' && (
            <Loader2 style={{ 
              margin: '0 auto', 
              height: '3rem', 
              width: '3rem', 
              color: '#3b82f6', 
              animation: 'spin 1s linear infinite',
              marginBottom: '1rem' 
            }} />
          )}
          {status === 'success' && (
            <CheckCircle style={{ 
              margin: '0 auto', 
              height: '3rem', 
              width: '3rem', 
              color: '#10b981', 
              marginBottom: '1rem' 
            }} />
          )}
          {status === 'error' && (
            <XCircle style={{ 
              margin: '0 auto', 
              height: '3rem', 
              width: '3rem', 
              color: '#ef4444', 
              marginBottom: '1rem' 
            }} />
          )}
          <h1 style={{ 
            fontSize: '1.5rem', 
            fontWeight: 'bold', 
            color: 'var(--color-text)', 
            marginBottom: '0.5rem' 
          }}>
            {status === 'loading' && 'Authorizing...'}
            {status === 'success' && 'Authorization Successful!'}
            {status === 'error' && 'Authorization Failed!'}
          </h1>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <p style={{ fontSize: '1.125rem', color: 'var(--color-text-subtle)' }}>
            {message}
          </p>
          
          {status === 'success' && (
            <div style={{
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '0.5rem',
              padding: '1rem'
            }}>
              <p style={{ fontSize: '0.875rem', color: '#166534' }}>
                This window will close automatically in a few seconds.
              </p>
            </div>
          )}
          
          {status === 'error' && (
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '0.5rem',
              padding: '1rem'
            }}>
              <p style={{ fontSize: '0.875rem', color: '#dc2626' }}>
                Please close this window and try again.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MCPOAuthCallbackPage;
